import React from 'react';
import { BarChart2, DollarSign, Calendar } from 'lucide-react';
import ToolResult from '../../../../../components/ui/ToolResult';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения детализации налоговых платежей
 */
const TaxBreakdownSection = ({ taxDetails, results, formData }) => {
  if (!taxDetails || !results) return null;

  const { incomeBreakdown, taxBreakdown } = taxDetails;
  
  // Определяем, используется ли прогрессивная шкала НДФЛ
  const isProgressiveTaxRate = 
    formData.taxResidency === 'resident' && 
    formData.incomeType === 'salary' && 
    results.grossIncome > 5000000 &&
    !formData.isIp &&
    !formData.isSelfEmployed;

  return (
    <ToolResult title="Детализация дохода и налога">
      <div className="mb-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Представлена детальная разбивка дохода и удержанного налога по периодам.
        </p>
      </div>

      {/* Таблица с детализацией */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Период</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">До налогообложения</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">НДФЛ</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">После налогообложения</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 whitespace-nowrap flex items-center">
                <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
                <span>За месяц</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(incomeBreakdown.monthly)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(results.taxAmount / 12)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{formatCurrency(incomeBreakdown.afterTax.monthly)}</td>
            </tr>
            <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 whitespace-nowrap flex items-center">
                <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
                <span>За квартал</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(incomeBreakdown.monthly * 3)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(results.taxAmount / 4)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{formatCurrency(incomeBreakdown.afterTax.monthly * 3)}</td>
            </tr>
            <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 whitespace-nowrap flex items-center">
                <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
                <span>За полугодие</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(incomeBreakdown.monthly * 6)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(results.taxAmount / 2)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{formatCurrency(incomeBreakdown.afterTax.monthly * 6)}</td>
            </tr>
            <tr className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 font-medium">
              <td className="px-4 py-3 whitespace-nowrap flex items-center">
                <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
                <span>За год</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(incomeBreakdown.annual)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(results.taxAmount)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{formatCurrency(incomeBreakdown.afterTax.annual)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Прогрессивная шкала НДФЛ (если применяется) */}
      {isProgressiveTaxRate && (
        <div className="mb-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
            Расчет по прогрессивной шкале
          </h3>
          
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">Диапазон дохода</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Ставка</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма дохода</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма налога</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 whitespace-nowrap">До 5 млн ₽</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">13%</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(Math.min(5000000, results.taxableIncome))}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(taxBreakdown.base)}</td>
                </tr>
                <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 whitespace-nowrap">Свыше 5 млн ₽</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">15%</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(Math.max(0, results.taxableIncome - 5000000))}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(taxBreakdown.progressive)}</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 font-medium">
                  <td className="px-4 py-3 whitespace-nowrap">Всего</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(results.effectiveTaxRate, 2)}%</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{formatCurrency(results.taxableIncome)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(results.taxAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Информация по режиму налогообложения */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <DollarSign className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            {formData.isIp ? (
              <>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Налогообложение индивидуального предпринимателя</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.ipTaxSystem === 'osn' && 'При общей системе налогообложения (ОСНО) ИП уплачивает НДФЛ по ставке 13% с доходов за вычетом расходов.'}
                  {formData.ipTaxSystem === 'usn_income' && 'При УСН "Доходы" ИП уплачивает налог по ставке 6% от доходов без учета расходов.'}
                  {formData.ipTaxSystem === 'usn_income_expenses' && 'При УСН "Доходы минус расходы" ИП уплачивает налог по ставке 15% от доходов за вычетом расходов.'}
                  {formData.ipTaxSystem === 'eshn' && 'При едином сельскохозяйственном налоге (ЕСХН) ИП уплачивает налог по ставке 6% от доходов за вычетом расходов.'}
                  {formData.ipTaxSystem === 'psn' && 'При патентной системе налогообложения (ПСН) ИП уплачивает фиксированную стоимость патента вместо НДФЛ.'}
                  {formData.ipTaxSystem === 'patent' && 'При приобретении патента ИП уплачивает фиксированную стоимость патента в размере 6% от потенциально возможного годового дохода.'}
                </p>
              </>
            ) : formData.isSelfEmployed ? (
              <>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Налогообложение самозанятых</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Самозанятые уплачивают налог на профессиональный доход (НПД) в размере 4% при оказании услуг физическим лицам и 6% при работе с организациями и ИП. 
                  В расчете применена ставка 4%. НПД заменяет НДФЛ и не требует подачи декларации.
                </p>
              </>
            ) : (
              <>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Налогообложение физических лиц</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.taxResidency === 'resident' ? 
                    (formData.incomeType === 'salary' && results.grossIncome > 5000000 ?
                      'В России с 2021 года действует прогрессивная шкала НДФЛ для доходов от трудовой деятельности: 13% на доходы до 5 млн рублей в год и 15% на превышение этой суммы.' :
                      'Для большинства доходов налоговых резидентов РФ действует ставка НДФЛ 13%.') :
                    'Для налоговых нерезидентов РФ действует ставка НДФЛ 15%.'
                  }
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default TaxBreakdownSection;